import { OverlayLayerContext, useOverlayLayer } from "./OverlayLayer";
import { ContextMenu as BaseContextMenu } from "@base-ui/react/context-menu";
import type { ComponentProps } from "react";
import { cn } from "../lib/cn";
import { DropdownMenu, type DropdownMenuContentProps } from "./DropdownMenu";
import { popupClassName } from "./Popover";

function Content({
  className,
  side = "right",
  align = "start",
  sideOffset = 2,
  alignOffset = 0,
  ...props
}: DropdownMenuContentProps) {
  const layer = useOverlayLayer("dropdown");
  return (
    <OverlayLayerContext value={layer}>
      <BaseContextMenu.Portal>
        <BaseContextMenu.Positioner
          style={{ zIndex: layer }}
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className="z-dropdown outline-none"
        >
          <BaseContextMenu.Popup
            data-slot="context-menu-content"
            className={cn(
              popupClassName,
              "min-w-48 max-h-[var(--available-height)] overflow-y-auto p-1",
              className,
            )}
            {...props}
          />
        </BaseContextMenu.Positioner>
      </BaseContextMenu.Portal>
    </OverlayLayerContext>
  );
}

function isEditing(target: EventTarget | null) {
  return target instanceof Element && !!target.closest("input, textarea, [contenteditable=true]");
}

function Trigger(props: ComponentProps<typeof BaseContextMenu.Trigger>) {
  return (
    <BaseContextMenu.Trigger
      {...props}
      onContextMenuCapture={(event) => {
        props.onContextMenuCapture?.(event);
        if (isEditing(event.target)) event.stopPropagation();
      }}
      onTouchStartCapture={(event) => {
        props.onTouchStartCapture?.(event);
        if (isEditing(event.target)) event.stopPropagation();
      }}
      onKeyDown={(event) => {
        props.onKeyDown?.(event);
        if (event.defaultPrevented || isEditing(event.target)) return;
        if (event.key !== "ContextMenu" && !(event.shiftKey && event.key === "F10")) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        event.currentTarget.dispatchEvent(
          new MouseEvent("contextmenu", {
            bubbles: true,
            cancelable: true,
            clientX: rect.left + 12,
            clientY: rect.top + Math.min(rect.height, 24),
          }),
        );
      }}
    />
  );
}

/** Same menu items as DropdownMenu; Base UI owns pointer placement and keyboard navigation. */
export const ContextMenu = {
  ...DropdownMenu,
  Root: BaseContextMenu.Root,
  Trigger,
  Content,
};
